import { Link } from 'react-router-dom';

const features = [
  { label: 'Minpaku Registration Support', typical: true, management: false, cleaning: false, ours: true },
  { label: 'Ryokan License Application Experience', typical: false, management: false, cleaning: false, ours: true },
  { label: 'Real Airbnb Operation Experience', typical: false, management: true, cleaning: false, ours: true },
  { label: 'Revenue Optimization Strategy', typical: false, management: true, cleaning: false, ours: true },
  { label: 'Cleaning System', typical: false, management: false, cleaning: true, ours: true },
  { label: 'Accounting Support System', typical: false, management: false, cleaning: false, ours: true },
  { label: 'End-to-End Business Support', typical: false, management: false, cleaning: false, ours: true },
];

const columns = [
  { key: 'typical', label: 'Typical Legal\nConsultant', color: 'text-slate-400' },
  { key: 'management', label: 'Management\nCompany', color: 'text-slate-400' },
  { key: 'cleaning', label: 'Cleaning\nCompany', color: 'text-slate-400' },
  { key: 'ours', label: 'Our\nService', color: 'text-emerald-600', highlight: true },
];

const approaches = [
  {
    icon: 'ri-file-text-line',
    title: 'Legal-Only Service',
    description:
      'Traditional consultants handle paperwork and licensing — but once the permit is issued, you\'re on your own. No operational guidance, no revenue strategy, no ongoing support.',
    tag: 'Limited Scope',
    tagColor: 'bg-red-100 text-red-600',
    border: 'border-slate-200',
  },
  {
    icon: 'ri-building-line',
    title: 'Management-Only Service',
    description:
      'Property management companies handle day-to-day operations, but they don\'t assist with licensing, accounting, or the strategic setup needed to launch a profitable business.',
    tag: 'Partial Coverage',
    tagColor: 'bg-amber-100 text-amber-600',
    border: 'border-slate-200',
  },
  {
    icon: 'ri-stack-line',
    title: 'Our Full-Stack Approach',
    description:
      'We cover every stage — from licensing and setup to daily operations, cleaning, revenue optimization, and accounting support. One team, one point of contact, complete coverage.',
    tag: 'Complete Solution',
    tagColor: 'bg-emerald-100 text-emerald-700',
    border: 'border-emerald-300',
    highlight: true,
  },
];

export default function ComparisonSection() {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Section Header ── */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full mb-4 border border-emerald-200">
            <i className="ri-bar-chart-grouped-line text-sm"></i>
            <span className="text-sm font-semibold tracking-wide uppercase">Competitive Advantage</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 leading-tight">
            Why We Are Different from<br className="hidden md:block" /> Other Tokyo Minpaku Services
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            Most services cover only one piece of the puzzle. We cover all of them — from licensing to daily operations.
          </p>
        </div>

        {/* ── Comparison Table ── */}
        <div className="overflow-x-auto mb-20 rounded-2xl shadow-xl border border-slate-200">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="bg-slate-900">
                <th className="text-left px-6 py-5 text-slate-300 font-semibold text-sm w-1/3">Feature</th>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`px-4 py-5 text-center text-sm font-semibold whitespace-pre-line leading-snug ${
                      col.highlight
                        ? 'bg-gradient-to-b from-emerald-600 to-teal-600 text-white'
                        : 'text-slate-400'
                    }`}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {features.map((row, i) => (
                <tr
                  key={row.label}
                  className={`border-t border-slate-100 transition-colors duration-150 ${
                    i % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'
                  }`}
                >
                  <td className="px-6 py-4 text-slate-700 font-medium text-sm">{row.label}</td>
                  {columns.map((col) => {
                    const val = row[col.key as keyof typeof row] as boolean;
                    return (
                      <td
                        key={col.key}
                        className={`px-4 py-4 text-center ${
                          col.highlight ? 'bg-emerald-50/60' : ''
                        }`}
                      >
                        {val ? (
                          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${col.highlight ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                            <i className="ri-check-line text-white text-sm"></i>
                          </span>
                        ) : (
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-100">
                            <i className="ri-close-line text-slate-300 text-sm"></i>
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Not Just Paperwork ── */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
              Not Just Paperwork.{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-500">
                Real Business Support.
              </span>
            </h3>
            <p className="text-slate-500 text-lg max-w-xl mx-auto">
              Understand the difference before you choose a service.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {approaches.map((item) => (
              <div
                key={item.title}
                className={`relative p-8 rounded-2xl border-2 ${item.border} ${
                  item.highlight
                    ? 'bg-gradient-to-br from-emerald-50 to-teal-50 shadow-lg'
                    : 'bg-white shadow-sm'
                } transition-all duration-300 hover:shadow-xl`}
              >
                {item.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 bg-emerald-600 text-white text-xs font-bold rounded-full shadow-md whitespace-nowrap">
                      ★ Recommended
                    </span>
                  </div>
                )}
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-5 ${item.tagColor}`}>
                  {item.tag}
                </div>
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-4 ${
                  item.highlight ? 'bg-emerald-600' : 'bg-slate-100'
                }`}>
                  <i className={`${item.icon} text-2xl ${item.highlight ? 'text-white' : 'text-slate-400'}`}></i>
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h4>
                <p className="text-slate-600 leading-relaxed text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Case-Based Storytelling ── */}
        <div className="relative rounded-2xl overflow-hidden shadow-2xl">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/4a4fb6dd1b3bef42f2d0471be81ee5c7.jpg"
              alt="Traditional house in Ota City Tokyo"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/30"></div>
          </div>

          <div className="relative px-10 py-16 md:px-16 md:py-20 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/20 border border-emerald-400/40 rounded-full mb-6">
              <i className="ri-map-pin-2-line text-emerald-400 text-sm"></i>
              <span className="text-emerald-300 text-xs font-semibold tracking-widest uppercase">Ota City, Tokyo</span>
            </div>

            <h3 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
              Based on Real Operational<br />Experience in Ota City
            </h3>

            <div className="space-y-4 mb-8">
              {[
                'We launched our own traditional lodging in Ota City — navigating every permit, inspection, and platform setup ourselves.',
                'We\'ve experienced the exact challenges our clients face: slow approvals, guest communication, pricing fluctuations, and cleaning logistics.',
                'This firsthand knowledge means our advice is grounded in reality — not theory or textbooks.',
              ].map((text, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="mt-1 w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full bg-emerald-500">
                    <i className="ri-check-line text-white text-xs"></i>
                  </span>
                  <p className="text-white/85 text-sm leading-relaxed">{text}</p>
                </div>
              ))}
            </div>

            <blockquote className="border-l-4 border-emerald-400 pl-5 mb-8">
              <p className="text-white/90 italic text-base leading-relaxed">
                "Operated by an active Tokyo host with real-world experience. This is not theory — this is practical, hands-on support."
              </p>
            </blockquote>

            <Link
              to="/tokyo-airbnb-support#contact"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              <span>Book a Free Consultation</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center bg-gradient-to-r from-[#F5F1E8] to-white p-8 md:p-12 rounded-lg border-2 border-[#B8860B]/30 shadow-lg">
          <h3 className="text-2xl md:text-3xl font-bold text-[#8B4513] mb-4">
            Ready to Experience the Difference?
          </h3>
          <p className="text-gray-700 mb-8 max-w-2xl mx-auto leading-relaxed">
            Join property owners who have transformed their Tokyo real estate into successful short-term rental businesses with our comprehensive <Link to="/tokyo-airbnb-support" className="text-[#B8860B] hover:text-[#8B4513] font-semibold underline">Tokyo minpaku support</Link> and <Link to="/airbnb-management-tokyo" className="text-[#B8860B] hover:text-[#8B4513] font-semibold underline">Airbnb management Tokyo</Link> services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/tokyo-airbnb-support#contact"
              className="px-8 py-4 bg-gradient-to-r from-[#8B4513] to-[#A0522D] text-white font-semibold rounded-lg hover:shadow-xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              Get Free Consultation
            </Link>
            <Link
              to="/minpaku-registration-tokyo"
              className="px-8 py-4 bg-white text-[#8B4513] font-semibold rounded-lg border-2 border-[#B8860B] hover:bg-[#F5F1E8] hover:shadow-lg transition-all duration-300 whitespace-nowrap"
            >
              Learn Registration Process
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}